<div class="absolute inset-0 flex flex-col overflow-hidden">
  <div
    class="bg-default dark:bg-transparent p-6 sm:p-10 sm:pt-12 sm:pb-28 flex flex-col sm:flex-row sm:justify-between sm:items-center"
  >
    <div class="flex-1 min-w-0">
      <div class="flex items-center mb-2">
        <a
          class="inline-flex items-center text-secondary font-medium"
          [routerLink]="'/program-list'"
        >
          <mat-icon
            class="icon-size-5 text-secondary"
            [svgIcon]="'heroicons_solid:chevron-left'"
          ></mat-icon>
          <span class="ml-1">Back</span>
        </a>
      </div>
      <h2 class="text-3xl md:text-4xl font-extrabold leading-tight truncate">
        {{ isEditMode ? "Edit Program" : "Create Program" }}
      </h2>
    </div>
  </div>

  <div
    class="flex-auto -mt-16 bg-card p-6 sm:p-10 rounded-t-2xl shadow overflow-y-auto"
    cdkScrollable
  >
    <form
      class="bg-white dark:bg-card shadow rounded-lg p-6 space-y-10"
      [formGroup]="addProgramForm"
      (ngSubmit)="onFormSubmit()"
    >
      <section>
        <h3 class="text-xl font-semibold border-t pt-6 mb-4">
          Program Information
        </h3>
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <mat-checkbox formControlName="isActive">Active Program</mat-checkbox>
          <div></div>
          <br />

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Program Name</mat-label>
            <input matInput formControlName="programName" required />
            <mat-error
              *ngIf="addProgramForm.get('programName').hasError('required')"
              >Program name is required</mat-error
            >
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Program Category</mat-label>
            <mat-select formControlName="programCategory" required>
              <mat-option>-- Select Category --</mat-option>
              <mat-option
                *ngFor="let category of categories"
                [value]="category.id"
              >
                {{ category.categoryName || category.name }}
              </mat-option>
            </mat-select>
            <mat-error
              *ngIf="addProgramForm.get('programCategory').hasError('required')"
            >
              Category is required
            </mat-error>
            <mat-hint *ngIf="categories.length === 0"
              >No categories available</mat-hint
            >
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Age Group</mat-label>
            <input
              matInput
              formControlName="ageGroup"
              (keydown)="
                onAgeInput($event, addProgramForm.get('ageGroup').value)
              "
              required
              placeholder="e.g., 5-9"
            />
            <mat-hint align="start">Enter age range like 5-9</mat-hint>
            <mat-error
              *ngIf="addProgramForm.get('ageGroup').hasError('required')"
            >
              Age group is required e.g., 5-19
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-span-full">
            <mat-label>Program Description</mat-label>
            <textarea
              matInput
              formControlName="programDescription"
              rows="3"
              required
              maxlength="255"
            >
            </textarea>
            <mat-hint align="end">
              {{
                addProgramForm.get("programDescription")?.value?.length || 0
              }}/255
            </mat-hint>
            <mat-error
              *ngIf="
                addProgramForm.get('programDescription')?.hasError('required')
              "
            >
              Description is required
            </mat-error>
            <mat-error
              *ngIf="
                addProgramForm.get('programDescription')?.hasError('maxlength')
              "
            >
              Description cannot exceed 255 characters
            </mat-error>
          </mat-form-field>
        </div>
      </section>

      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">
          Location Information
        </h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Community</mat-label>
            <mat-select
              formControlName="community"
              required
              readonly="isEditMode"
            >
              <mat-option *ngFor="let list of communities" [value]="list.key">
                {{ list.value }}
              </mat-option>
            </mat-select>
            <mat-error
              *ngIf="addProgramForm.get('community').hasError('required')"
            >
              Community is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Venue of the program</mat-label>
            <input matInput formControlName="location" required />
            <mat-error
              *ngIf="addProgramForm.get('location').hasError('required')"
              >Location is required</mat-error
            >
          </mat-form-field>

          <mat-form-field appearance="outline" class="sm:col-span-2">
            <mat-label>Instructor(s)</mat-label>
            <textarea matInput formControlName="instructor" rows="2"></textarea>
          </mat-form-field>
        </div>
      </section>

      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">
          Date Information
        </h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Start Date</mat-label>
            <input
              matInput
              [matDatepicker]="startPicker"
              formControlName="startDate"
              required
              [readonly]="isEditMode"
              [min]="minStartDate"
            />
            <mat-datepicker-toggle
              matSuffix
              [for]="startPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>

            <!-- Validation Messages -->
            <mat-error
              *ngIf="addProgramForm.get('startDate').hasError('required')"
            >
              Start date is required
            </mat-error>
            <mat-error
              *ngIf="addProgramForm.get('startDate').hasError('pastDate')"
            >
              Start date cannot be in the past
            </mat-error>
          </mat-form-field>

          <!-- End Date Field -->
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>End Date</mat-label>
            <input
              matInput
              [matDatepicker]="endPicker"
              formControlName="endDate"
              required
              [readonly]="isEditMode"
              [min]="addProgramForm.get('startDate').value || minStartDate"
            />
            <mat-datepicker-toggle
              matSuffix
              [for]="endPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>

            <!-- Validation Messages -->
            <mat-error
              *ngIf="addProgramForm.get('endDate').hasError('required')"
            >
              End date is required
            </mat-error>
            <mat-error *ngIf="addProgramForm.hasError('invalidDateRange')">
              End date must be after start date
            </mat-error>
          </mat-form-field>
        </div>
      </section>

      <section>
        <div class="pt-8 border-t">
          <h3 class="text-xl font-semibold mb-6">Schedule</h3>
          <div class="space-y-4">
            <div
              *ngFor="let day of daysOfWeek"
              class="flex flex-col gt-xs:flex-row gap-6 items-center"
            >
              <mat-checkbox
                formControlName="{{ day.field }}"
                class="w-full gt-xs:w-1/3"
                [readonly]="isEditMode"
                >{{ day.name }}</mat-checkbox
              >
              <div
                class="flex-1 grid grid-cols-2 gap-4 w-full"
                *ngIf="addProgramForm.get(day.field).value"
              >
                <mat-form-field>
                  <mat-label>Start Time</mat-label>
                  <input
                    matTooltip="Please Select a Start Time"
                    matInput
                    type="time"
                    formControlName="startTime{{ day.name }}"
                    class="big-time-input"
                    required
                    [readonly]="isEditMode"
                  />
                  <mat-error
                    *ngIf="
                      addProgramForm
                        .get('startTime' + day.name)
                        .hasError('required')
                    "
                  >
                    Start time is required
                  </mat-error>
                </mat-form-field>

                <mat-form-field>
                  <mat-label>End Time</mat-label>
                  <input
                    matTooltip="Please Select an End Time"
                    matInput
                    type="time"
                    formControlName="endTime{{ day.name }}"
                    class="big-time-input"
                    required
                    [readonly]="isEditMode"
                  />
                  <mat-error
                    *ngIf="
                      addProgramForm
                        .get('endTime' + day.name)
                        .hasError('required')
                    "
                  >
                    End time is required
                  </mat-error>
                </mat-form-field>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">
          Available Spaces
        </h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Available Spaces</mat-label>
            <input
              matInput
              type="tel"
              formControlName="availableSpace"
              min="1"
              required
              [readonly]="isEditMode"
              (keydown)="onPhoneInput($event)"
            />
            <mat-error
              *ngIf="addProgramForm.get('availableSpace').hasError('required')"
              >Available spaces is required</mat-error
            >
            <mat-error
              *ngIf="addProgramForm.get('availableSpace').hasError('min')"
              >Must be at least 1</mat-error
            >
          </mat-form-field>

          <!-- <mat-form-field appearance="outline" class="w-full">
            <mat-label>Waiting List Spaces</mat-label>
            <input
              matInput
              type="tel"
              formControlName="availableWaitingSpace"
              min="0"
              [readonly]="isEditMode"
              (keydown)="onPhoneInput($event)"
            />
          </mat-form-field> -->
        </div>
      </section>

      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">Fees</h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <!-- Fee Application Method -->
          <div class="sm:col-span-2">
            <p class="text-sm mb-2">Apply Fee By*</p>
            <div class="flex space-x-4">
              <!-- Apply Fee by Program -->
              <mat-checkbox
                formControlName="applyFeeByProgram"
                [disabled]="
                  isEditMode ||
                  (addProgramForm.get('applyFeeByClass').value &&
                    !addProgramForm.get('applyFeeByProgram').value)
                "
                class="mr-4"
                (change)="onFeeMethodChange('program')"
              >
                Apply Fee by Program
              </mat-checkbox>

              <!-- Apply Fee by Class -->
              <mat-checkbox
                formControlName="applyFeeByClass"
                [disabled]="
                  isEditMode ||
                  (addProgramForm.get('applyFeeByProgram').value &&
                    !addProgramForm.get('applyFeeByClass').value)
                "
                (change)="onFeeMethodChange('class')"
              >
                Apply Fee by Class
              </mat-checkbox>
            </div>

            <!-- Error message -->
            <mat-error *ngIf="addProgramForm.hasError('feeMethodRequired')">
              Please select how to apply fees
            </mat-error>
          </div>

          <!-- Program Fee Field -->
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Program Fee ($)</mat-label>
            <input
              matInput
              type="tel"
              formControlName="programFee"
              min="0.5"
              step="0.01"
              required
              [readonly]="isEditMode"
              (keydown)="
                onFeeInput($event, addProgramForm.get('programFee').value)
              "
            />

            <!-- Error for required -->
            <mat-error
              *ngIf="addProgramForm.get('programFee').hasError('required')"
            >
              Program fee is required
            </mat-error>

            <!-- Error for minimum -->
            <mat-error *ngIf="addProgramForm.get('programFee').hasError('min')">
              Minimum program fee is $0.5
            </mat-error>
          </mat-form-field>
        </div>
      </section>
      <!-- Program Media & Outcomes Section -->
      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">
          Program Media & Outcomes
        </h3>
        <div class="space-y-6">
          <!-- Program Image Upload -->
          <div class="w-full">
            <label class="block text-sm font-medium mb-2 text-gray-700">
              Program Image
            </label>

            <!-- Upload Area -->
            <div class="flex flex-col sm:flex-row gap-4 items-start">
              <!-- Image preview -->
              <div *ngIf="programImagePreview" class="relative">
                <img
                  [src]="programImagePreview"
                  alt="Program preview"
                  class="w-32 h-32 object-cover rounded-lg border"
                  (error)="handleImageError($event)"
                />
                <button
                  type="button"
                  mat-icon-button
                  color="warn"
                  class="absolute -top-2 -right-2 bg-white rounded-full shadow-md"
                  (click)="removeImage()"
                  [disabled]="isImageUploading || isUploading"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>

              <!-- Upload Controls -->
              <div class="flex-1">
                <div class="flex flex-col gap-2">
                  <!-- File Input -->
                  <input
                    type="file"
                    #fileInput
                    (change)="onImageSelected($event)"
                    accept="image/*"
                    class="hidden"
                  />

                  <button
                    type="button"
                    mat-stroked-button
                    color="primary"
                    (click)="fileInput.click()"
                    [disabled]="isImageUploading || isUploading"
                  >
                    <mat-icon class="mr-2">cloud_upload</mat-icon>
                    {{ programImagePreview ? "Change Image" : "Upload Image" }}
                  </button>

                  <!-- Upload Progress -->
                  <div *ngIf="isUploading" class="w-full">
                    <mat-progress-bar
                      mode="determinate"
                      [value]="uploadPercent"
                    ></mat-progress-bar>
                    <p class="text-xs text-gray-600 mt-1">
                      Uploading: {{ uploadPercent }}%
                    </p>
                  </div>

                  <!-- Loading Spinner -->
                  <div
                    *ngIf="isImageUploading && !isUploading"
                    class="flex items-center gap-2"
                  >
                    <mat-spinner diameter="20"></mat-spinner>
                    <span class="text-sm text-gray-600"
                      >Processing image...</span
                    >
                  </div>

                  <!-- Help Text -->
                  <p class="text-xs text-gray-500">
                    Supported formats: JPG, PNG, GIF. Max size: 5MB
                  </p>
                </div>
              </div>
            </div>

            <!-- Current Image URL (for debugging) -->
            <div
              *ngIf="addProgramForm.get('programImage').value"
              class="mt-2 p-2 bg-gray-100 rounded text-xs"
            >
              <!-- <strong>Image URL:</strong>
              <span class="break-all">{{
                addProgramForm.get("programImage").value
              }}</span> -->
            </div>
          </div>

          <!-- Key Outcomes -->
          <div class="w-full">
            <label class="block text-sm font-medium mb-2 text-gray-700"
              >Key Outcomes</label
            >
            <mat-form-field appearance="outline" class="w-full">
              <textarea
                matInput
                formControlName="keyOutcome"
                rows="4"
                placeholder="What participants will learn and achieve from this program..."
                class="rich-text-area"
              ></textarea>
            </mat-form-field>
            <mat-hint
              >Describe the main benefits and learning outcomes</mat-hint
            >
          </div>

          <!-- Curriculum Outline -->
          <div class="w-full">
            <label class="block text-sm font-medium mb-2 text-gray-700"
              >Curriculum Outline</label
            >
            <mat-form-field appearance="outline" class="w-full">
              <textarea
                matInput
                formControlName="curriculumOutline"
                rows="6"
                placeholder="Outline the program curriculum, topics covered, and learning progression..."
                class="rich-text-area"
              ></textarea>
            </mat-form-field>
            <mat-hint
              >Detailed breakdown of course content and structure</mat-hint
            >
          </div>
        </div>
      </section>

      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">Policies</h3>
        <div class="space-y-4">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Policy</mat-label>
            <textarea
              matInput
              formControlName="cancellationPolicy"
              rows="3"
              maxlength="255"
            ></textarea>
            <mat-hint align="end">
              {{
                addProgramForm.get("cancellationPolicy")?.value?.length || 0
              }}/255
            </mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Payment Terms</mat-label>
            <textarea
              matInput
              formControlName="paymentTerm"
              rows="3"
              maxlength="255"
            ></textarea>
            <mat-hint align="end">
              {{ addProgramForm.get("paymentTerm")?.value?.length || 0 }}/255
            </mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Payment Disclaimer</mat-label>
            <textarea
              matInput
              formControlName="paymentDisclaimer"
              rows="3"
              maxlength="255"
            ></textarea>
            <mat-hint align="end">
              {{
                addProgramForm.get("paymentDisclaimer")?.value?.length || 0
              }}/255
            </mat-hint>
          </mat-form-field>
        </div>
      </section>

      <section>
        <h3 class="text-xl font-semibold mb-4 border-t pt-6">
          Additional Information
        </h3>
        <div class="space-y-4">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>What participants will gain</mat-label>
            <textarea
              matInput
              formControlName="gainDescription"
              rows="3"
              maxlength="255"
            ></textarea>
            <mat-hint align="end">
              {{
                addProgramForm.get("gainDescription")?.value?.length || 0
              }}/255
            </mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Notes</mat-label>
            <textarea
              matInput
              formControlName="notes"
              rows="3"
              maxlength="255"
            ></textarea>
            <mat-hint align="end">
              {{ addProgramForm.get("notes")?.value?.length || 0 }}/255
            </mat-hint>
          </mat-form-field>
        </div>
      </section>

      <div class="pt-6 border-t flex justify-center gap-4">
        <button
          mat-raised-button
          color="primary"
          [disabled]="addProgramForm.invalid || pageLoader"
        >
          <span *ngIf="!pageLoader">{{
            isEditMode ? "Update Program" : "Create Program"
          }}</span>
          <mat-spinner *ngIf="pageLoader" diameter="20"></mat-spinner>
        </button>
        <button
          mat-button
          type="button"
          (click)="router.navigate(['/program-list'])"
        >
          Cancel
        </button>
      </div>
    </form>

    <div class="loaderMain" *ngIf="pageLoader">
      <div class="flex flex-col items-center justify-center mt-10">
        <img
          src="assets/TrailFusion/Trail-Fusion-logo.jpg"
          class="LoaderImage"
        />
        <p class="loaderText mt-4 text-center">{{ pageLoaderText }}</p>
      </div>
    </div>
  </div>
</div>
